import React from 'react';
import { Input, Icon } from 'antd';
import get from 'lodash/get';
import { BOOKING_STATUS, GARAGE_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';
import { MARK_WORK_COMPLETE_SUCCESS, MARK_WORK_COMPLETE_FAILURE } from './constants';

import SelectCancelReasonModal from './components/SelectReasonModal';

import {
  PATCH_BOOKING_SUCCESS,
  CHARGE_PAYMENT_ERROR,
  CHARGE_PAYMENT_SUCCESS,
  CHARGE_INSTALMENT_ERROR,
  CHARGE_INSTALMENT_SUCCESS,
  ADD_NOTE_SUCCESS,
} from '../../constants';
import { KLARNA_FINANCING } from '../../KlarnaReauthorisationAlert/logic';

const isStatusAvailable = (booking, nextStatus) => {
  const nextMasterStatusList = get(booking, 'masterStatus.nextMasterStatusList');
  if (!Array.isArray(nextMasterStatusList)) return false;
  return !!nextMasterStatusList.find(({ masterStatusName }) => masterStatusName === nextStatus);
};

const isCurrentStatus = (booking, status) => get(booking, 'masterStatus.masterStatusName') === status;
const isCustomerCnD = (booking) => {
  const deliveryType = get(booking, 'collectionDeliveryOption');
  return deliveryType === 'CustomerC&D';
};

export default [
  {
    type: 'move.garage',
    text: 'Move at/to Garage',
    canDisplay: (booking) => {
      const currentStateName = get(booking, 'masterStatus.masterStatusName');
      const availableStates = ['AWAITING_PICKUP', 'BEING_COLLECTED', 'TRANSPORT_TO_GARAGE', 'WORK_COMPLETE'];
      if (isCustomerCnD(booking)) return ['ALLOCATED', 'WORK_COMPLETE'].includes(currentStateName);
      return availableStates.includes(currentStateName) && !isCustomerCnD(booking);
    },
    className: 'functional_moveToGarage-modal',
    render: (booking) => (
      <p>
        Confirm moving booking status to {isCustomerCnD(booking) ? 'awaiting drop off' : 'AT_GARAGE'}: #
        {booking.bookingId}?
      </p>
    ),
    payload: (actionStore, booking) => {
      const currentStateName = get(booking, 'masterStatus.masterStatusName');

      return {
        PATCH_BOOKING: {
          masterStatusId:
            isCustomerCnD(booking) && currentStateName === 'ALLOCATED'
              ? BOOKING_STATUS.AWAITING_PICKUP
              : BOOKING_STATUS.AT_GARAGE,
        },
      };
    },
    modalBusyUntil: [PATCH_BOOKING_SUCCESS],
  },
  {
    type: 'work.complete',
    text: 'Work complete',
    canDisplay: (booking) =>
      isStatusAvailable(booking, 'WORK_COMPLETE') &&
      ![GARAGE_STATUS.FIXTER_REVIEW, GARAGE_STATUS.CUSTOMER_REVIEW, GARAGE_STATUS.CUSTOMER_REVIEWED].includes(
        booking.garageStatus
      ),
    className: 'functional_moveToWorkComplete-modal',
    render: () => (
      <div>
        <h5>Are you sure the garage has finished working on this vehicle?</h5>
        <p>
          Note: Clicking &quot;Yes&quot; will notify the customer and prompt them to make a payment for this
          booking.
        </p>
      </div>
    ),
    payload: () => ({ MARK_WORK_COMPLETE: {} }),
    modalBusyUntil: [MARK_WORK_COMPLETE_SUCCESS, MARK_WORK_COMPLETE_FAILURE],
  },
  {
    type: 'move.pickup',
    text: 'Back to awaiting pickup',
    canDisplay: (booking) =>
      [BOOKING_STATUS.TRANSPORT_TO_GARAGE, BOOKING_STATUS.BEING_COLLECTED, BOOKING_STATUS.AT_GARAGE].includes(
        get(booking, 'masterStatus.masterStatusId')
      ) &&
      ![GARAGE_STATUS.DRAFT, GARAGE_STATUS.FIXTER_REVIEW, GARAGE_STATUS.CUSTOMER_REVIEW].includes(
        booking.garageStatus
      ),
    render: (booking, actionStore) => {
      const statusName = get(booking, 'masterStatus.masterStatusName', 'unknown');
      actionStore[
        'move.pickup.reason'
      ] = `no reason typed for moving booking from ${statusName} to AWAITING_PICKUP`;
      const getEnhancedNote = (e) => {
        const message = `${e.target.value} - listed as the reason when moving this booking from ${statusName} to AWAITING_PICKUP.`;
        actionStore['move.pickup.reason'] = message;
      };
      return (
        <div>
          <p>
            Confirm moving booking status to {isCustomerCnD(booking) ? 'Allocated' : 'AWAITING_PICKUP'}: #
            {booking.bookingId}?
          </p>
          <Input placeholder="Type a reason here" onChange={(e) => getEnhancedNote(e)} />
        </div>
      );
    },
    payload: (actionStore, booking) => ({
      PATCH_BOOKING: {
        masterStatusId: isCustomerCnD(booking) ? BOOKING_STATUS.ALLOCATED : BOOKING_STATUS.AWAITING_PICKUP,
      },
      ADD_NOTE: {
        ownerType: 'booking',
        content: actionStore['move.pickup.reason'],
      },
    }),
    modalBusyUntil: [PATCH_BOOKING_SUCCESS],
  },
  {
    type: 'charge.payment',
    text: 'Charge Payment',
    title: 'Charge Payment',
    canDisplay: (booking) => {
      const currentStateName = get(booking, 'masterStatus.masterStatusName');
      const paymentStatus = get(booking, 'paymentMethod.status');
      const availableStates = ['WORK_COMPLETE', 'BEING_RETURNED', 'UNPAID'];
      return availableStates.includes(currentStateName) && paymentStatus !== 'paid';
    },
    render: ({ bookingId }) => <p>Confirm you would like to charge payment for booking: #{bookingId}?</p>,
    payload: (actionStore, booking, idempotencyKey) => ({
      CHARGE_PAYMENT: { idempotencyKey },
    }),
    modalBusyUntil: [CHARGE_PAYMENT_ERROR, CHARGE_PAYMENT_SUCCESS],
  },
  {
    type: 'charge.installment',
    text: 'Skip Payment',
    title: 'Please confirm the following:',
    okText: 'Confirm',
    canDisplay: (booking) => {
      const currentStateName = get(booking, 'masterStatus.masterStatusName');
      const paymentStatus = get(booking, 'paymentMethod.status');
      const availableStates = ['WORK_COMPLETE', 'BEING_RETURNED', 'UNPAID'];
      const isNotKlarnaPayment = get(booking, 'paymentMethod.paymentMethod.source') !== KLARNA_FINANCING;
      return isNotKlarnaPayment && availableStates.includes(currentStateName) && paymentStatus !== 'paid';
    },
    render: () => {
      const ListItem = ({ children }) => (
        <li style={{ marginBottom: 12, position: 'relative' }}>{children}</li>
      );
      const CheckIcon = () => (
        <Icon
          type="check"
          style={{
            color: '#4fd44f',
            fontSize: 16,
            marginRight: 10,
            position: 'absolute',
            top: '50%',
            left: -24,
            marginTop: -6,
            display: 'block',
          }}
        />
      );
      return (
        <>
          <ul style={{ listStyleType: 'none', padding: '0 0 0 24px' }}>
            <ListItem>
              <CheckIcon />
              You've checked the customer has approved their installment plan
            </ListItem>
            <ListItem>
              <CheckIcon />
              You've recieved the “Phone order approval notification“ email from Splitit sent to
              "support@fxtr.co.uk"
            </ListItem>
            <ListItem>
              <CheckIcon />
              You've recorded the installment plan reference number in the notes section on this page
            </ListItem>
          </ul>
        </>
      );
    },
    payload: (actionStore, booking, idempotencyKey) => ({
      CHARGE_INSTALMENT: { idempotencyKey },
    }),
    modalBusyUntil: [CHARGE_INSTALMENT_ERROR, CHARGE_INSTALMENT_SUCCESS],
  },
  {
    type: 'cancel.booking',
    text: 'Cancel booking',
    title: `Confirm cancellation of booking?`,
    render: (booking, actionStore) => <SelectCancelReasonModal booking={booking} actionStore={actionStore} />,
    canDisplay: (booking) => isStatusAvailable(booking, 'CANCELLED'),
    payload: (actionStore, booking, idempotencyKey) => {
      const isFree = actionStore['cancel.booking.free'] !== 'no';
      return {
        CANCEL_BOOKING: {
          patch: {
            masterStatusId: BOOKING_STATUS.CANCELLED,
            free: isFree,
            payGarageCD:
              actionStore['cancel.booking.payGarageCD'] === undefined
                ? true
                : actionStore['cancel.booking.payGarageCD'],
          },
          note: {
            ownerType: 'booking',
            content: actionStore['cancel.booking.reason'],
            noteType: 'BookingCancelled',
          },
          idempotencyKey: !isFree ? idempotencyKey : undefined,
        },
      };
    },
    modalBusyUntil: [PATCH_BOOKING_SUCCESS, ADD_NOTE_SUCCESS],
  },
  {
    type: 'close.booking',
    text: 'Close Booking',
    canDisplay: (booking) =>
      booking.paymentMethod.status === 'paid' &&
      (isCurrentStatus(booking, 'PAID') || isCurrentStatus(booking, 'WORK_COMPLETE')) &&
      booking.transportStatus === 'DELIVERED',
    render: (booking) => <p>Confirm you would like to close booking: #{booking.bookingId}?</p>,
    payload: () => ({ PATCH_BOOKING: { masterStatusId: BOOKING_STATUS.CLOSED } }),
    modalBusyUntil: [PATCH_BOOKING_SUCCESS],
  },
];
